/**
 * LDAP Integration Store
 * 
 * Manages LDAP/Active Directory integration state including:
 * - Tree expansion/selection state (UI)
 * - Anomaly tracking
 * - Health monitoring
 * - 3D graph node linking
 * 
 * Peer reviewed: Gemini 3 Pro, GPT-4o
 * 
 * Architecture notes:
 * - Data fetching via TanStack Query (not stored here)
 * - UI state (expansion, selection, highlight) in Zustand
 * - DN (Distinguished Name) is the primary key
 * - Separation of concerns: UI state vs server data
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  LDAPAnomaly,
  LDAPHealth,
  ConnectionStatus,
  AnomalySeverity,
  LDAPObjectType,
} from '../types/ldap';

// ============================================================================
// State Interface
// ============================================================================

interface LDAPState {
  // ----------------------------------------------------------------------------
  // Tree Navigation State
  // ----------------------------------------------------------------------------
  
  /** Set of expanded node DNs */
  expandedDns: Set<string>;
  
  /** Currently selected node DN (single selection) */
  selectedDn: string | null;
  
  /** Multi-selected DNs (for bulk operations) */
  multiSelectedDns: Set<string>;
  
  /** Highlighted DN (e.g., from 3D view hover) */
  highlightedDn: string | null;
  
  /** Search/filter query */
  searchQuery: string;
  
  /** Object type filter */
  objectTypeFilter: LDAPObjectType[] | null;
  
  // ----------------------------------------------------------------------------
  // Health & Connection State
  // ----------------------------------------------------------------------------
  
  /** Connection status */
  connectionStatus: ConnectionStatus;
  
  /** Full health data (updated via Socket.io) */
  health: LDAPHealth | null;
  
  /** Updates available indicator (tree changed externally) */
  updatesAvailable: boolean;
  
  /** Pending update count */
  pendingUpdateCount: number;
  
  // ----------------------------------------------------------------------------
  // Anomaly State
  // ----------------------------------------------------------------------------
  
  /** All detected anomalies (keyed by ID) */
  anomalies: Map<string, LDAPAnomaly>;
  
  /** Currently viewing anomaly ID */
  selectedAnomalyId: string | null;
  
  /** Anomaly severity filter */
  anomalySeverityFilter: AnomalySeverity[] | null;
  
  /** Show only unacknowledged anomalies */
  showOnlyUnacknowledged: boolean;
  
  // ----------------------------------------------------------------------------
  // 3D Integration State
  // ----------------------------------------------------------------------------
  
  /** DN to Graph Node ID mapping */
  dnToGraphNodeMap: Map<string, string>;
  
  /** Graph Node ID to DN mapping (reverse lookup) */
  graphNodeToDnMap: Map<string, string>;
  
  /** Whether to sync selection with 3D view */
  syncWith3DView: boolean;
  
  // ----------------------------------------------------------------------------
  // UI State
  // ----------------------------------------------------------------------------
  
  /** Is LDAP panel visible? */
  isPanelOpen: boolean;
  
  /** Active tab */
  activeTab: 'browser' | 'anomalies' | 'health' | 'membership';
  
  /** Browser view mode */
  viewMode: 'tree' | 'list' | 'grid';
  
  /** Is loading initial data? */
  isInitialLoading: boolean;
}

// ============================================================================
// Actions Interface
// ============================================================================

interface LDAPActions {
  // Tree Navigation
  toggleExpansion: (dn: string) => void;
  expandNode: (dn: string) => void;
  collapseNode: (dn: string) => void;
  expandAll: (dns: string[]) => void;
  collapseAll: () => void;
  
  setSelectedDn: (dn: string | null) => void;
  toggleMultiSelect: (dn: string) => void;
  clearMultiSelect: () => void;
  setHighlightedDn: (dn: string | null) => void;
  
  setSearchQuery: (query: string) => void;
  setObjectTypeFilter: (types: LDAPObjectType[] | null) => void;
  
  // Health & Connection
  setConnectionStatus: (status: ConnectionStatus) => void;
  setHealth: (health: LDAPHealth) => void;
  markUpdatesAvailable: (count: number) => void;
  clearUpdatesAvailable: () => void;
  
  // Anomalies
  setAnomalies: (anomalies: LDAPAnomaly[]) => void;
  addAnomaly: (anomaly: LDAPAnomaly) => void;
  removeAnomaly: (id: string) => void;
  acknowledgeAnomaly: (id: string, by: string) => void;
  setSelectedAnomalyId: (id: string | null) => void;
  setAnomalySeverityFilter: (severities: AnomalySeverity[] | null) => void;
  setShowOnlyUnacknowledged: (show: boolean) => void;
  
  // 3D Integration
  registerGraphNodeMapping: (dn: string, graphNodeId: string) => void;
  unregisterGraphNodeMapping: (dn: string) => void;
  clearGraphNodeMappings: () => void;
  setSyncWith3DView: (sync: boolean) => void;
  
  // Find graph node for DN
  getGraphNodeIdForDn: (dn: string) => string | undefined;
  getDnForGraphNodeId: (graphNodeId: string) => string | undefined;
  
  // UI
  setIsPanelOpen: (open: boolean) => void;
  setActiveTab: (tab: LDAPState['activeTab']) => void;
  setViewMode: (mode: LDAPState['viewMode']) => void;
  setIsInitialLoading: (loading: boolean) => void;
  
  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: LDAPState = {
  // Tree Navigation
  expandedDns: new Set(),
  selectedDn: null,
  multiSelectedDns: new Set(),
  highlightedDn: null,
  searchQuery: '',
  objectTypeFilter: null,
  
  // Health & Connection
  connectionStatus: 'disconnected',
  health: null,
  updatesAvailable: false,
  pendingUpdateCount: 0,
  
  // Anomalies
  anomalies: new Map(),
  selectedAnomalyId: null,
  anomalySeverityFilter: null,
  showOnlyUnacknowledged: false,
  
  // 3D Integration
  dnToGraphNodeMap: new Map(),
  graphNodeToDnMap: new Map(),
  syncWith3DView: true,
  
  // UI
  isPanelOpen: true,
  activeTab: 'browser',
  viewMode: 'tree',
  isInitialLoading: true,
};

// ============================================================================
// Store Creation
// ============================================================================

export const useLDAPStore = create<LDAPState & LDAPActions>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        // ----------------------------------------------------------------------
        // Tree Navigation Actions
        // ----------------------------------------------------------------------
        
        toggleExpansion: (dn: string) => {
          set((state) => {
            if (state.expandedDns.has(dn)) {
              state.expandedDns.delete(dn);
            } else {
              state.expandedDns.add(dn);
            }
          });
        },
        
        expandNode: (dn: string) => {
          set((state) => {
            state.expandedDns.add(dn);
          });
        },
        
        collapseNode: (dn: string) => {
          set((state) => {
            state.expandedDns.delete(dn);
          });
        },
        
        expandAll: (dns: string[]) => {
          set((state) => {
            dns.forEach((dn) => state.expandedDns.add(dn));
          });
        },
        
        collapseAll: () => {
          set((state) => {
            state.expandedDns.clear();
          });
        },
        
        setSelectedDn: (dn: string | null) => {
          set((state) => {
            state.selectedDn = dn;
          });
        },
        
        toggleMultiSelect: (dn: string) => {
          set((state) => {
            if (state.multiSelectedDns.has(dn)) {
              state.multiSelectedDns.delete(dn);
            } else {
              state.multiSelectedDns.add(dn);
            }
          });
        },
        
        clearMultiSelect: () => {
          set((state) => {
            state.multiSelectedDns.clear();
          });
        },
        
        setHighlightedDn: (dn: string | null) => {
          set((state) => {
            state.highlightedDn = dn;
          });
        },
        
        setSearchQuery: (query: string) => {
          set((state) => {
            state.searchQuery = query;
          });
        },
        
        setObjectTypeFilter: (types: LDAPObjectType[] | null) => {
          set((state) => {
            state.objectTypeFilter = types;
          });
        },

        // ----------------------------------------------------------------------
        // Health & Connection Actions
        // ----------------------------------------------------------------------
        
        setConnectionStatus: (status: ConnectionStatus) => {
          set((state) => {
            state.connectionStatus = status;
          });
        },
        
        setHealth: (health: LDAPHealth) => {
          set((state) => {
            state.health = health;
            state.connectionStatus = health.connection.status;
          });
        },
        
        markUpdatesAvailable: (count: number) => {
          set((state) => {
            state.updatesAvailable = true;
            state.pendingUpdateCount = count;
          });
        },
        
        clearUpdatesAvailable: () => {
          set((state) => {
            state.updatesAvailable = false;
            state.pendingUpdateCount = 0;
          });
        },

        // ----------------------------------------------------------------------
        // Anomaly Actions
        // ----------------------------------------------------------------------
        
        setAnomalies: (anomalies: LDAPAnomaly[]) => {
          set((state) => {
            state.anomalies.clear();
            anomalies.forEach((a) => state.anomalies.set(a.id, a));
          });
        },
        
        addAnomaly: (anomaly: LDAPAnomaly) => {
          set((state) => {
            state.anomalies.set(anomaly.id, anomaly);
          });
        },
        
        removeAnomaly: (id: string) => {
          set((state) => {
            state.anomalies.delete(id);
          });
        },
        
        acknowledgeAnomaly: (id: string, by: string) => {
          set((state) => {
            const anomaly = state.anomalies.get(id);
            if (anomaly) {
              anomaly.acknowledged = true;
              anomaly.acknowledgedBy = by;
              anomaly.acknowledgedAt = new Date().toISOString();
            }
          });
        },
        
        setSelectedAnomalyId: (id: string | null) => {
          set((state) => {
            state.selectedAnomalyId = id;
          });
        },
        
        setAnomalySeverityFilter: (severities: AnomalySeverity[] | null) => {
          set((state) => {
            state.anomalySeverityFilter = severities;
          });
        },
        
        setShowOnlyUnacknowledged: (show: boolean) => {
          set((state) => {
            state.showOnlyUnacknowledged = show;
          });
        },

        // ----------------------------------------------------------------------
        // 3D Integration Actions
        // ----------------------------------------------------------------------
        
        registerGraphNodeMapping: (dn: string, graphNodeId: string) => {
          set((state) => {
            state.dnToGraphNodeMap.set(dn, graphNodeId);
            state.graphNodeToDnMap.set(graphNodeId, dn);
          });
        },
        
        unregisterGraphNodeMapping: (dn: string) => {
          set((state) => {
            const graphNodeId = state.dnToGraphNodeMap.get(dn);
            if (graphNodeId) {
              state.graphNodeToDnMap.delete(graphNodeId);
            }
            state.dnToGraphNodeMap.delete(dn);
          });
        },
        
        clearGraphNodeMappings: () => {
          set((state) => {
            state.dnToGraphNodeMap.clear();
            state.graphNodeToDnMap.clear();
          });
        },
        
        setSyncWith3DView: (sync: boolean) => {
          set((state) => {
            state.syncWith3DView = sync;
          });
        },
        
        getGraphNodeIdForDn: (dn: string) => {
          return get().dnToGraphNodeMap.get(dn);
        },
        
        getDnForGraphNodeId: (graphNodeId: string) => {
          return get().graphNodeToDnMap.get(graphNodeId);
        },

        // ----------------------------------------------------------------------
        // UI Actions
        // ----------------------------------------------------------------------
        
        setIsPanelOpen: (open: boolean) => {
          set((state) => {
            state.isPanelOpen = open;
          });
        },
        
        setActiveTab: (tab: LDAPState['activeTab']) => {
          set((state) => {
            state.activeTab = tab;
          });
        },
        
        setViewMode: (mode: LDAPState['viewMode']) => {
          set((state) => {
            state.viewMode = mode;
          });
        },
        
        setIsInitialLoading: (loading: boolean) => {
          set((state) => {
            state.isInitialLoading = loading;
          });
        },

        // ----------------------------------------------------------------------
        // Reset
        // ----------------------------------------------------------------------
        
        reset: () => {
          set(() => ({ ...initialState }));
        },
      }))
    ),
    { name: 'ldap-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

/** Select expanded DNs as array */
export const selectExpandedDns = (state: LDAPState & LDAPActions) => 
  Array.from(state.expandedDns);

/** Select multi-selected DNs as array */
export const selectMultiSelectedDns = (state: LDAPState & LDAPActions) => 
  Array.from(state.multiSelectedDns);

/** Select all anomalies as array */
export const selectAnomalies = (state: LDAPState & LDAPActions) => 
  Array.from(state.anomalies.values());

/** Select filtered anomalies */
export const selectFilteredAnomalies = (state: LDAPState & LDAPActions) => {
  let anomalies = Array.from(state.anomalies.values());
  
  if (state.anomalySeverityFilter && state.anomalySeverityFilter.length > 0) {
    anomalies = anomalies.filter((a) => 
      state.anomalySeverityFilter?.includes(a.severity)
    );
  }
  
  if (state.showOnlyUnacknowledged) {
    anomalies = anomalies.filter((a) => !a.acknowledged);
  }
  
  return anomalies;
};

/** Select anomalies by severity */
export const selectAnomaliesBySeverity = (
  state: LDAPState & LDAPActions,
  severity: AnomalySeverity
) => {
  return Array.from(state.anomalies.values()).filter(
    (a) => a.severity === severity
  );
};

/** Select anomaly counts by severity */
export const selectAnomalyCounts = (state: LDAPState & LDAPActions) => {
  const anomalies = Array.from(state.anomalies.values());
  return {
    critical: anomalies.filter((a) => a.severity === 'critical').length,
    high: anomalies.filter((a) => a.severity === 'high').length,
    medium: anomalies.filter((a) => a.severity === 'medium').length,
    low: anomalies.filter((a) => a.severity === 'low').length,
    info: anomalies.filter((a) => a.severity === 'info').length,
    total: anomalies.length,
    unacknowledged: anomalies.filter((a) => !a.acknowledged).length,
  };
};

/** Is a specific DN expanded? */
export const selectIsExpanded = (state: LDAPState & LDAPActions, dn: string) =>
  state.expandedDns.has(dn);

/** Is a specific DN selected? */
export const selectIsSelected = (state: LDAPState & LDAPActions, dn: string) =>
  state.selectedDn === dn;

/** Is a specific DN multi-selected? */
export const selectIsMultiSelected = (
  state: LDAPState & LDAPActions,
  dn: string
) => state.multiSelectedDns.has(dn);

/** Select connection status */
export const selectConnectionStatus = (state: LDAPState & LDAPActions) =>
  state.connectionStatus;

/** Select health stats */
export const selectHealthStats = (state: LDAPState & LDAPActions) =>
  state.health?.sync ?? null;

/** Select connection health */
export const selectConnectionHealth = (state: LDAPState & LDAPActions) =>
  state.health?.connection ?? null;

// ============================================================================
// Hooks for common patterns
// ============================================================================

/**
 * Hook to check if a node is expanded
 */
export const useIsNodeExpanded = (dn: string) =>
  useLDAPStore((state) => state.expandedDns.has(dn));

/**
 * Hook to check if a node is selected
 */
export const useIsNodeSelected = (dn: string) =>
  useLDAPStore((state) => state.selectedDn === dn);

/**
 * Hook to get graph node ID for an LDAP DN
 */
export const useGraphNodeForDn = (dn: string) =>
  useLDAPStore((state) => state.dnToGraphNodeMap.get(dn));

